/* ==========================================================================
   Premium Product Page Experience — Strydify
   --------------------------------------------------------------------------
   Purely additive, defensive enhancements for the product page:
     - initHeroLoadIn      staggered fade/rise for the above-the-fold hero
     - initReveal           scroll entrance animations for info-column content
     - initSectionReveal    whole-section scroll reveal for storytelling
                             sections below the fold (materials, lifestyle,
                             brand story, comparison table, testimonials,
                             FAQ, reviews, final CTA)
     - initTilt              subtle mouse-tilt + glare on product photography
     - initFloat             gentle idle float on the hero product image
     - initParallaxGlow      soft scroll-linked glow behind the hero image
     - initLifestyleParallax subtle scroll parallax on the lifestyle gallery
     - init3DHint            "drag to rotate" hint on the native 3D viewer
     - initCartSuccess       success pulse on Add to Cart, after a real add
     - initScrollToBuyBox    smooth-scrolls "Shop now" CTAs back to the
                              existing, unmodified product form

   This script never calls preventDefault/stopPropagation on the product
   form, never touches cart or checkout submission logic, and always no-ops
   quietly if an expected element isn't present. Every effect is skipped
   under prefers-reduced-motion, and pointer-driven effects only run on
   hover-capable, fine-pointer (non-touch) devices.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canHover =
    window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var SECTION_REVEAL_SKIP_IDS = ['shopify-section-main'];

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function shouldSkipSection(id) {
    if (!id) return false;
    if (SECTION_REVEAL_SKIP_IDS.indexOf(id) > -1) return true;
    if (id.indexOf('horizontal_ticker') > -1) return true;
    if (id.indexOf('1783386839d5917fa2') > -1) return true;
    return false;
  }

  function initHeroLoadIn() {
    try {
      var mainSection = document.getElementById('shopify-section-main');
      if (!mainSection) return;

      var media = mainSection.querySelector('.product__media-wrapper');
      var info = mainSection.querySelector('.product__info-container');

      var els = [];
      if (media) els.push(media);
      if (info) {
        var infoChildren = info.querySelectorAll(':scope > *');
        els = els.concat(Array.prototype.slice.call(infoChildren));
      }
      if (!els.length) return;

      if (reduceMotion) {
        els.forEach(function (el) {
          el.classList.add('pfx-hero-in');
        });
        return;
      }

      els.forEach(function (el, i) {
        el.classList.add('pfx-hero-el');
        el.style.transitionDelay = Math.min(i * 65, 480) + 'ms';
      });

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          els.forEach(function (el) {
            el.classList.add('pfx-hero-in');
          });
        });
      });
    } catch (e) {
      /* fail silently */
    }
  }

  function initReveal(scope) {
    try {
      var root = (scope || document).querySelector('.product__info-container');
      if (!root) return;

      var selectors = [
        '.product__title',
        '.product-page-price',
        '.product__text',
        '.product__inventory',
        '.urgency-text',
        '.product-form__input',
        '.quantity-breaks-container',
        '.product-form__buttons',
        '.product__pickup-availabilities',
        '.emoji-benefits-container',
        '.payment-badges-block',
        '.icon-with-text',
        '.product__description',
        '.product__accordion',
        '.product__sku',
        '.complementary-products',
        '.product__view-details'
      ];

      var els = Array.prototype.slice.call(root.querySelectorAll(selectors.join(',')));
      if (!els.length) return;

      if (reduceMotion || !('IntersectionObserver' in window)) {
        els.forEach(function (el) {
          el.classList.add('pfx-visible');
        });
        return;
      }

      els.forEach(function (el, i) {
        if (el.classList.contains('pfx-reveal')) return;
        el.classList.add('pfx-reveal');
        el.style.transitionDelay = Math.min(i * 40, 280) + 'ms';
      });

      var io = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('pfx-visible');
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
      );

      els.forEach(function (el) {
        io.observe(el);
      });
    } catch (e) {
      /* fail silently */
    }
  }

  function initSectionReveal() {
    try {
      var main = document.getElementById('MainContent');
      if (!main) return;

      var sections = Array.prototype.slice.call(main.querySelectorAll(':scope > .shopify-section'));
      if (!sections.length) return;

      if (reduceMotion || !('IntersectionObserver' in window)) {
        sections.forEach(function (sec) {
          sec.classList.add('pfx-visible');
        });
        return;
      }

      var variantIndex = 0;
      var toObserve = [];
      sections.forEach(function (sec) {
        if (shouldSkipSection(sec.id) || sec.dataset.pfxSectionInit) return;
        sec.dataset.pfxSectionInit = 'true';
        sec.classList.add('pfx-section-reveal', variantIndex % 2 === 0 ? 'pfx-section-reveal--a' : 'pfx-section-reveal--b');
        variantIndex++;
        toObserve.push(sec);
      });

      if (!toObserve.length) return;

      var io = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('pfx-visible');
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
      );

      toObserve.forEach(function (sec) {
        io.observe(sec);
      });
    } catch (e) {
      /* fail silently */
    }
  }

  function initTilt(scope) {
    try {
      if (!canHover || reduceMotion) return;

      var containers = (scope || document).querySelectorAll(
        '.product__media-wrapper .product-media-container.media-type-image'
      );

      containers.forEach(function (container) {
        if (container.dataset.pfxTiltInit) return;
        if (container.querySelector('.image-magnify-hover')) return;

        var mediaBox = container.querySelector('.media');
        if (!mediaBox) return;

        container.dataset.pfxTiltInit = 'true';
        container.classList.add('pfx-tilt-enabled');

        var raf = null;

        function onMove(e) {
          var rect = container.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          var x = (e.clientX - rect.left) / rect.width;
          var y = (e.clientY - rect.top) / rect.height;
          var rotateY = (x - 0.5) * 6;
          var rotateX = (0.5 - y) * 6;

          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function () {
            mediaBox.style.transform =
              'perspective(1400px) rotateX(' + rotateX.toFixed(2) + 'deg) rotateY(' + rotateY.toFixed(2) + 'deg)';
            container.style.setProperty('--pfx-x', (x * 100).toFixed(1) + '%');
            container.style.setProperty('--pfx-y', (y * 100).toFixed(1) + '%');
          });
        }

        function onEnter() {
          container.classList.add('pfx-tilt-active');
        }

        function onLeave() {
          container.classList.remove('pfx-tilt-active');
          mediaBox.style.transform = '';
        }

        container.addEventListener('mouseenter', onEnter);
        container.addEventListener('mousemove', onMove);
        container.addEventListener('mouseleave', onLeave);
      });
    } catch (e) {
      /* fail silently */
    }
  }

  function initFloat(scope) {
    try {
      if (!canHover || reduceMotion) return;

      var container = (scope || document).querySelector(
        '.product__media-wrapper .product-media-container.media-type-image'
      );
      if (!container || container.dataset.pfxFloatInit) return;
      container.dataset.pfxFloatInit = 'true';
      container.classList.add('pfx-float');
    } catch (e) {
      /* fail silently */
    }
  }

  function initParallaxGlow(scope) {
    try {
      if (reduceMotion) return;

      var wrapper = (scope || document).querySelector('.product__media-wrapper');
      if (!wrapper) return;

      var container = wrapper.querySelector('.product-media-container');
      if (!container || container.dataset.pfxGlowInit) return;
      container.dataset.pfxGlowInit = 'true';

      var glow = document.createElement('div');
      glow.className = 'pfx-parallax-glow';
      glow.setAttribute('aria-hidden', 'true');
      container.prepend(glow);

      var ticking = false;

      function update() {
        var rect = wrapper.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var progress = (vh - rect.top) / (vh + rect.height);
        var offset = (progress - 0.5) * 36;
        glow.style.setProperty('--pfx-parallax-offset', offset.toFixed(1) + 'px');
        ticking = false;
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    } catch (e) {
      /* fail silently */
    }
  }

  function initLifestyleParallax() {
    try {
      if (reduceMotion) return;
      if (window.matchMedia && !window.matchMedia('(min-width: 700px)').matches) return;

      var items = document.querySelectorAll('.premium-lifestyle__item[data-pfx-depth]');
      if (!items.length) return;

      var ticking = false;

      function update() {
        var vh = window.innerHeight || document.documentElement.clientHeight;
        items.forEach(function (item) {
          var depth = parseFloat(item.getAttribute('data-pfx-depth')) || 0;
          var rect = item.getBoundingClientRect();
          var center = rect.top + rect.height / 2 - vh / 2;
          var offset = center * -0.06 * depth;
          item.style.setProperty('--pfx-lifestyle-offset', offset.toFixed(1) + 'px');
        });
        ticking = false;
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    } catch (e) {
      /* fail silently */
    }
  }

  function init3DHint(scope) {
    try {
      var modelContainers = (scope || document).querySelectorAll(
        '.product-media-container.media-type-model'
      );

      modelContainers.forEach(function (container) {
        if (container.dataset.pfxHintInit) return;
        container.dataset.pfxHintInit = 'true';

        var hint = document.createElement('div');
        hint.className = 'pfx-3d-hint';
        hint.setAttribute('aria-hidden', 'true');
        hint.textContent = 'Drag to rotate';
        container.appendChild(hint);

        var dismiss = function () {
          container.classList.add('pfx-interacted');
        };

        container.addEventListener('pointerdown', dismiss, { once: true });
      });
    } catch (e) {
      /* fail silently */
    }
  }

  function initCartSuccess() {
    try {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        if (!data || data.source !== 'product-form') return;
        var btn = document.querySelector('.atc-button');
        if (!btn) return;
        btn.classList.remove('pfx-success');
        void btn.offsetWidth;
        btn.classList.add('pfx-success');
        window.setTimeout(function () {
          btn.classList.remove('pfx-success');
        }, 700);
      });
    } catch (e) {
      /* fail silently */
    }
  }

  function initScrollToBuyBox() {
    try {
      var triggers = document.querySelectorAll('.pfx-scroll-to-buybox');
      if (!triggers.length) return;

      triggers.forEach(function (trigger) {
        if (trigger.dataset.pfxScrollInit) return;
        trigger.dataset.pfxScrollInit = 'true';

        trigger.addEventListener('click', function (e) {
          var target =
            document.querySelector('#shopify-section-main .product-form') ||
            document.querySelector('#shopify-section-main .product__info-wrapper') ||
            document.getElementById('MainContent');
          if (!target) return;

          e.preventDefault();
          target.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'center'
          });

          var btn = document.querySelector('.atc-button');
          if (btn) {
            window.setTimeout(function () {
              btn.classList.remove('pfx-highlight');
              void btn.offsetWidth;
              btn.classList.add('pfx-highlight');
              window.setTimeout(function () {
                btn.classList.remove('pfx-highlight');
              }, 1200);
            }, reduceMotion ? 0 : 500);
          }
        });
      });
    } catch (e) {
      /* fail silently */
    }
  }

  function initAll(scope) {
    initReveal(scope);
    initTilt(scope);
    initFloat(scope);
    initParallaxGlow(scope);
    init3DHint(scope);
  }

  ready(function () {
    initHeroLoadIn();
    initAll(document);
    initSectionReveal();
    initLifestyleParallax();
    initCartSuccess();
    initScrollToBuyBox();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
    initScrollToBuyBox();
  });
})();
